import { DataPoints, getPdfImageFilePath } from "../api";

export type PdfImageSource = {
    blobPath?: string;
    key: string;
    order: number;
    src: string;
};

const normalizePdfImagePath = (value?: string) =>
    String(value ?? "")
        .trim()
        .replace(/^\/+/, "")
        .toLowerCase();

const trimStringArrayPreservingPositions = (values?: string[]) => values?.map(value => String(value ?? "").trim()) ?? [];
const filterNonEmptyStrings = (values?: string[]) => trimStringArrayPreservingPositions(values).filter(Boolean);
export const resolvePdfImages = (contentItem?: Pick<DataPoints, "pdf_image_urls" | "pdf_images">): PdfImageSource[] => {
    const pdfImageUrls = trimStringArrayPreservingPositions(contentItem?.pdf_image_urls);
    const pdfImages = trimStringArrayPreservingPositions(contentItem?.pdf_images);
    const totalImages = Math.max(pdfImageUrls.length, pdfImages.length);
    const resolvedImages: PdfImageSource[] = [];

    for (let index = 0; index < totalImages; index += 1) {
        const blobPath = pdfImageUrls[index];
        const dataUrl = pdfImages[index];
        const src = dataUrl || (blobPath ? getPdfImageFilePath(blobPath) : "");

        if (!src) {
            continue;
        }

        resolvedImages.push({
            blobPath,
            key: `${blobPath ?? "pdf-image"}-${index}`,
            order: index,
            src
        });
    }

    return resolvedImages;
};

export const takePdfImagesForRequestedPaths = (requestedPaths: string[], availableImages: PdfImageSource[], usedImageOrders: Set<number>): PdfImageSource[] => {
    const resolvedImages: PdfImageSource[] = [];
    let reuseCounter = 0;
    let fallbackCounter = 0;

    for (const requestedPath of filterNonEmptyStrings(requestedPaths)) {
        const normalizedRequestedPath = normalizePdfImagePath(requestedPath);
        const exactMatchImage = availableImages.find(image => image.blobPath && normalizePdfImagePath(image.blobPath) === normalizedRequestedPath);
        if (exactMatchImage) {
            usedImageOrders.add(exactMatchImage.order);
            resolvedImages.push({ ...exactMatchImage, key: `${exactMatchImage.key}-reuse-${reuseCounter++}` });
            continue;
        }
        const positionalMatch = availableImages.find(image => !usedImageOrders.has(image.order));

        if (positionalMatch) {
            usedImageOrders.add(positionalMatch.order);
            resolvedImages.push(positionalMatch);
            continue;
        }

        resolvedImages.push({
            blobPath: requestedPath,
            key: `${requestedPath}-fallback-${fallbackCounter}`,
            order: availableImages.length + fallbackCounter,
            src: getPdfImageFilePath(requestedPath)
        });
        fallbackCounter += 1;
    }

    return resolvedImages;
};

export const getUnusedPdfImages = (availableImages: PdfImageSource[], usedImageOrders: Set<number>): PdfImageSource[] =>
    availableImages.filter(image => !usedImageOrders.has(image.order));
